/**
 * Pagina publica del portal del cliente (Sprint 6 B).
 *
 * Ruta `/portal/:token`. NO requiere sesion ni auth context — la
 * autoridad la da el token. Mantiene el branding MSSPRD y reusa la
 * paleta del PDF ejecutivo.
 *
 * Estados:
 *   - loading
 *   - error (401 not_found, 410 expired/revoked, otros)
 *   - listo (muestra resumen + descarga PDF)
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { type PortalView, fetchPortalView, portalPdfUrl } from '../api/portal.js';
// ADR-041 D2: scoreColor extraido a DomainHeatMap para compartir con CaptureResult.
// Portal re-exporta desde ahi para no duplicar la logica.
import { scoreColor } from '../components/DomainHeatMap.js';

type ErrorKind = 'not_found' | 'expired' | 'revoked' | 'rate_limited' | 'pending' | 'unknown';

function classifyError(err: unknown): ErrorKind {
  const msg = err instanceof Error ? err.message : '';
  if (msg.startsWith('401:')) return 'not_found';
  if (msg.startsWith('410:')) {
    if (msg.includes('expired')) return 'expired';
    if (msg.includes('revoked')) return 'revoked';
    return 'expired';
  }
  if (msg.startsWith('429:')) return 'rate_limited';
  if (msg.startsWith('404:') && msg.includes('not_ready')) return 'pending';
  return 'unknown';
}

export function PortalPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PortalView | null>(null);
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchPortalView(token)
      .then(setView)
      .catch((err: unknown) => setErrorKind(classifyError(err)));
  }, [token]);

  if (errorKind) return <PortalError kind={errorKind} />;
  if (!view) return <PortalLoading />;

  return <PortalContent view={view} token={token!} />;
}

function PortalLoading(): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-sm text-slate-500">Cargando reporte...</p>
    </main>
  );
}

function PortalError({ kind }: { kind: ErrorKind }): JSX.Element {
  const titles: Record<ErrorKind, string> = {
    not_found: 'Enlace invalido',
    expired: 'Enlace expirado',
    revoked: 'Enlace revocado',
    rate_limited: 'Demasiados intentos',
    pending: 'Reporte en preparacion',
    unknown: 'No se pudo cargar el reporte',
  };
  const descriptions: Record<ErrorKind, string> = {
    not_found:
      'Este enlace no es valido. Verifique que copio la URL completa o solicite uno nuevo a su contacto MSSPRD.',
    expired:
      'Este enlace expiro. Solicite un nuevo enlace a su contacto MSSPRD para acceder al reporte actualizado.',
    revoked:
      'Este enlace fue revocado por MSSPRD. Si necesita acceso al reporte, contacte a su auditor.',
    rate_limited:
      'Demasiados intentos consecutivos. Espere unos minutos antes de volver a intentar.',
    pending: 'El reporte aun se esta generando. Vuelva a intentar en unos minutos.',
    unknown: 'Ocurrio un error inesperado. Vuelva a intentar mas tarde o contacte a MSSPRD.',
  };
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-700 text-white font-bold mb-4">
          C
        </div>
        <h1 className="text-xl font-semibold text-slate-900">{titles[kind]}</h1>
        <p className="mt-3 text-sm text-slate-600">{descriptions[kind]}</p>
        <p className="mt-6 text-xs text-slate-400">Cumple · un producto de MSSPRD</p>
      </div>
    </main>
  );
}

function PortalContent({ view, token }: { view: PortalView; token: string }): JSX.Element {
  return (
    <main className="min-h-screen bg-slate-50">
      <header className="bg-brand-700 text-white px-6 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 font-bold">
              AF
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70">
                Portal cliente · MSSPRD
              </p>
              <h1 className="text-xl font-semibold">{view.client.name}</h1>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/85">
            {view.site.name}
            {view.site.address ? ` · ${view.site.address}` : ''}
          </p>
          {view.completed_at && (
            <p className="text-xs text-white/70">
              Cerrado el {new Date(view.completed_at).toLocaleDateString('es-DO')}
            </p>
          )}
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6 flex items-center gap-6">
          <div className="text-5xl font-bold" style={{ color: scoreColor(view.global_score) }}>
            {view.global_score}
            <span className="text-base text-slate-500">/100</span>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Score global de seguridad</p>
            <p className="text-sm text-slate-600">
              Promedio ponderado de {view.instances.length} survey
              {view.instances.length === 1 ? '' : 's'} evaluado
              {view.instances.length === 1 ? '' : 's'} en sitio.
            </p>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3">
          <h2 className="font-semibold text-slate-900">Mapa de calor por dominio</h2>
          <ul className="space-y-2">
            {view.instances.flatMap((inst) =>
              Object.entries(inst.per_domain).map(([d, v]) => {
                const score = Number(v);
                return (
                  <li key={`${inst.id}-${d}`} className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-700">
                        {inst.template.code.toUpperCase()} · {d}
                      </span>
                      <span className="font-semibold" style={{ color: scoreColor(score) }}>
                        {Math.round(score)}/100
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-200">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${String(Math.max(0, Math.min(100, score)))}%`,
                          background: scoreColor(score),
                        }}
                      />
                    </div>
                  </li>
                );
              }),
            )}
          </ul>
        </div>

        {view.top_findings.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3">
            <h2 className="font-semibold text-slate-900">Top hallazgos criticos</h2>
            <ol className="space-y-2">
              {view.top_findings.map((f, idx) => (
                <li
                  key={`${f.domain}-${String(idx)}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm text-slate-800">{f.domain}</span>
                  <span className="text-sm font-bold" style={{ color: scoreColor(f.score) }}>
                    {Math.round(f.score)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="rounded-2xl bg-brand-700 text-white p-6 text-center">
          <h2 className="font-semibold text-lg">Descargue el reporte completo</h2>
          <p className="text-sm text-white/85 mt-1 mb-4">
            PDF ejecutivo con el detalle de hallazgos y recomendaciones priorizadas.
          </p>
          <a
            href={portalPdfUrl(token)}
            className="inline-block rounded-lg bg-white text-brand-700 px-5 py-3 text-sm font-semibold hover:bg-white/95"
          >
            Descargar PDF
          </a>
        </div>

        <footer className="text-center text-xs text-slate-400 pt-4">
          MSSPRD · Reporte confidencial · Cada acceso al portal queda auditado con IP, fecha y hora.
        </footer>
      </section>
    </main>
  );
}
